import ModalResponsive from '@/components/commons/modal-responsive';
import { ShippingMethodCheckoutType } from '@/components/sections/builder/elements/body/checkout-form/component/checkout-form';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { useGetShippingRates, useShippingCategory } from '@/hooks/api/storefront/orders';
import { cn, formatEstimateDelivery, formatIDR } from '@/lib/utils';
import { IShippingRates } from '@/types/area';
import { BaseShippingRatesPayload } from '@/types/http/orders/common';
import { AxiosError } from 'axios';
import Image from 'next/image';
import React from 'react';
import Cookies from 'js-cookie';

export type FormSelectShippingCheckoutMethodType = {
  shippingMethodId: string;
  shippingServiceId?: string;
};

export interface shippingRatesProps extends BaseShippingRatesPayload {
  checkout_id?: string;
}

type ModalShippingCheckoutProps = {
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  dataRates: shippingRatesProps;
  onSelect: (values: ShippingMethodCheckoutType) => void;
  disabled?: boolean;
};

export default function ModalShippingCheckout({
  openModal,
  setOpenModal,
  dataRates,
  onSelect,
  disabled,
}: ModalShippingCheckoutProps) {
  const [selectedCategory, setSelectedCategory] = React.useState<string | undefined>(undefined);
  const [selectedShipping, setSelectedShipping] = React.useState<string | undefined>(undefined);
  const [listShipping, setListShipping] = React.useState<IShippingRates[]>([]);

  const { data: listShippingCategory, isLoading: isLoadingCategory } = useShippingCategory({
    enabled: !!openModal,
  });

  const findLocationId = Cookies.get('_location_id');

  const { mutateAsync: getShippingRates, isPending: isLoadingShipping } = useGetShippingRates(
    findLocationId || '',
    {
      onSuccess: (response) => setListShipping(response.data as unknown as IShippingRates[]),
      onError: (error) => {
        setListShipping([]);
        setSelectedShipping(undefined);

        let message = 'Something went wrong';
        if (error instanceof AxiosError) {
          message = error.response?.data.message;
        }

        toast({
          title: 'Error',
          description: message,
          variant: 'destructive',
        });
      },
    }
  );

  const handleCategorySelect = (categoryId: string) => {
    if (!categoryId) return;

    const selectedCategory = listShippingCategory?.data.find(
      (item) => item.service_category_id === categoryId
    );

    if (
      selectedCategory?.name.toLowerCase() === 'instant' ||
      selectedCategory?.name.toLowerCase() === 'sameday'
    ) {
      if (disabled) {
        toast({
          title: 'Error',
          description: 'Please enter the pinpoint location for this shipping',
          variant: 'destructive',
        });
        return;
      }
    }

    setSelectedCategory(categoryId);

    if (categoryId) {
      getShippingRates({
        ...dataRates,
        service_category_id: Number(categoryId),
      });
    }
  };

  const handleShippingSelect = (serviceId: number) => {
    const selectedService = listShipping.find((item) => item.courier_service_id === serviceId);

    if (selectedService) {
      const result = {
        id: String(selectedService?.courier_id),
        name: selectedService?.courier_service_code,
        courir_category_id: selectedCategory,
        courier_code: selectedService?.courier_service_category,
        courier_logo: selectedService?.courier_logo,
        service: {
          id: String(selectedService?.courier_service_id),
          name: selectedService?.courier_service_name,
          label: `${selectedService?.eta_from} - ${selectedService?.eta_to}`,
          price: selectedService?.final_rates,
          shipping_insurance: selectedService?.shipping_insurance,
          is_cod_supported: selectedService?.is_cod_supported,
          cod_fee: selectedService?.cod_fee,
          cod_fee_shipping_insurance: selectedService?.cod_fee_shipping_insurance,
        },
        promotion: selectedService?.promotion,
      };

      onSelect(result);
    }

    setSelectedCategory(undefined);
  };

  const resetSelection = React.useCallback(() => {
    setSelectedCategory(undefined);
  }, []);

  return (
    <>
      <ModalResponsive
        openModal={openModal}
        setOpenModal={setOpenModal}
        title='Select Shipping'
        className='theme_font max-w-md'
      >
        <div className='px-6 pb-5'>
          {isLoadingCategory ? (
            <div className='flex w-full items-center justify-center'>
              <div className='flex w-full flex-col items-center justify-center gap-2'>
                <Skeleton className='h-8 w-full rounded-sm bg-gray-200' />
                <Skeleton className='h-8 w-full rounded-sm bg-gray-200' />
                <Skeleton className='h-8 w-full rounded-sm bg-gray-200' />
              </div>
            </div>
          ) : (
            listShippingCategory &&
            listShippingCategory?.data.length > 0 && (
              <Accordion
                type='single'
                collapsible
                className='w-full'
                onValueChange={(v) => (!v ? resetSelection() : handleCategorySelect(v))}
              >
                {listShippingCategory?.data?.map((category) => {
                  const disableCourier =
                    (category.name.toLowerCase() === 'instant' ||
                      category.name.toLowerCase() === 'sameday') &&
                    disabled;
                  return (
                    <AccordionItem
                      value={category.service_category_id}
                      key={category.service_category_id}
                    >
                      <AccordionTrigger
                        className={cn(
                          'w-full flex-row-reverse justify-between rounded-lg p-3',
                          disableCourier ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-accent'
                        )}
                        disabled={disableCourier}
                      >
                        <div
                          className={cn(
                            'justify-between text-sm font-medium',
                            disableCourier ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer'
                          )}
                        >
                          {category.name}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        {selectedCategory &&
                          (isLoadingShipping ? (
                            <div className='flex w-full flex-col items-center justify-center gap-2 px-2'>
                              <Skeleton className='h-8 w-full rounded-sm bg-gray-200' />
                              <Skeleton className='h-8 w-full rounded-sm bg-gray-200' />
                              <Skeleton className='h-8 w-full rounded-sm bg-gray-200' />
                            </div>
                          ) : listShipping && listShipping.length > 0 ? (
                            <>
                              <div className='max-h-96 overflow-y-auto px-2'>
                                <RadioGroup value={selectedShipping}>
                                  {listShipping.map((courier) => (
                                    <div
                                      key={courier.courier_service_id}
                                      className='flex items-center space-x-2 rounded-lg border-b px-4 py-2 hover:bg-accent'
                                      onClick={() => {
                                        setSelectedShipping(String(courier.courier_service_id));
                                        handleShippingSelect(courier.courier_service_id);
                                      }}
                                    >
                                      <Label
                                        htmlFor={String(courier.courier_service_id)}
                                        className='flex flex-1 cursor-pointer justify-between'
                                      >
                                        <div className='flex w-full items-center space-x-4'>
                                          {courier.courier_logo && (
                                            <Image
                                              src={courier.courier_logo}
                                              alt={courier.courier_service_name}
                                              width={40}
                                              height={40}
                                            />
                                          )}
                                          <div className='flex w-full items-center justify-between text-sm'>
                                            <div className='space-y-1'>
                                              <div className='flex items-center gap-2'>
                                                <p className='font-medium'>
                                                  {courier.courier_service_name}
                                                </p>
                                                {courier.is_cod_supported && (
                                                  <Badge className='rounded-sm px-1 py-0 text-[10px] font-normal'>
                                                    COD Support
                                                  </Badge>
                                                )}
                                              </div>
                                              <p className='text-xs text-muted-foreground'>
                                                Estimate:{' '}
                                                {formatEstimateDelivery(
                                                  courier.eta_from,
                                                  courier.eta_to
                                                )}
                                              </p>
                                            </div>
                                            <div className='flex items-center gap-x-2'>
                                              <div
                                                className={cn(
                                                  (courier.promotion?.promotion_value ||
                                                    courier.shipper_promotion?.promotion_value) &&
                                                    'flex items-center gap-2'
                                                )}
                                              >
                                                <p
                                                  className={cn(
                                                    'text-sm text-muted-foreground',
                                                    (courier.promotion ||
                                                      courier.shipper_promotion) &&
                                                      'line-through'
                                                  )}
                                                >
                                                  {formatIDR(courier.rates)}
                                                </p>
                                                <p
                                                  className={cn(
                                                    'c text-sm text-muted-foreground',
                                                    !(
                                                      courier.promotion || courier.shipper_promotion
                                                    ) && 'hidden'
                                                  )}
                                                >
                                                  {formatIDR(courier?.final_rates ?? 0)}
                                                </p>
                                              </div>
                                              <RadioGroupItem
                                                className='hidden'
                                                value={String(courier.courier_service_id)}
                                                id={String(courier.courier_service_id)}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      </Label>
                                    </div>
                                  ))}
                                  {listShipping.length === 0 && (
                                    <div className='flex min-h-[50px] w-full items-center justify-center'>
                                      <p className='text-sm'>No shipping available</p>
                                    </div>
                                  )}
                                </RadioGroup>
                              </div>
                            </>
                          ) : (
                            <div className='flex min-h-[50px] w-full items-center justify-center'>
                              <p className='text-sm'>No shipping available</p>
                            </div>
                          ))}
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )
          )}
        </div>
      </ModalResponsive>
    </>
  );
}
